import { useCheckoutStore, type RefundableStatus } from '@store/checkout-store';
import type { CheckoutPayload } from './types';

type QuotedRefundableState = {
  accepted: boolean;
  price: number;
};

function readBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function readPrice(value: unknown): number {
  const price = Number(value);
  return Number.isFinite(price) && price > 0 ? price : 0;
}

export function getQuotedRefundableFromCart(
  cart: CheckoutPayload | null | undefined
): QuotedRefundableState | null {
  if (!cart) return null;

  const accepted =
    readBoolean(cart.refundableAccepted) ??
    readBoolean(cart.packageConfigDetails?.refundableAccepted);

  if (accepted === undefined) return null;

  return {
    accepted,
    price: readPrice(
      cart.refundablePrice ?? cart.packageConfigDetails?.refundablePrice
    ),
  };
}

export function buildQuotedRefundableCartPatch({
  refundableStatus,
  isRefundableSelected,
  refundableAccepted,
  refundablePrice,
}: {
  refundableStatus: RefundableStatus;
  isRefundableSelected: boolean;
  refundableAccepted: boolean;
  refundablePrice: number | string | null;
}): Pick<CheckoutPayload, 'refundableAccepted' | 'refundablePrice'> | null {
  if (refundableStatus !== 'ready' || !isRefundableSelected) return null;

  return {
    refundableAccepted,
    refundablePrice:
      refundableAccepted && Number(refundablePrice) > 0
        ? Number(refundablePrice)
        : null,
  };
}

export function applyQuotedBonusMarkupToCheckoutStore(
  cart: CheckoutPayload | null | undefined
) {
  if (!cart) return;

  const type =
    cart.bonusMarkupType === 'fixed' || cart.bonusMarkupType === 'percentage'
      ? cart.bonusMarkupType
      : null;
  // A cart with no saved markup must not wipe what was typed on the review step
  // (the checkout form re-applies the cart on mount).
  if (!type) return;
  const value = Number(cart.bonusMarkupValue);

  useCheckoutStore
    .getState()
    .setBonusMarkup(
      type,
      type && Number.isFinite(value) && value > 0 ? value : null
    );
}

export function applyQuotedRefundableToCheckoutStore(
  cart: CheckoutPayload | null | undefined,
  cartId?: number | string | null
) {
  const quoted = getQuotedRefundableFromCart(cart);
  if (!quoted) return;

  const resolvedCartId = Number(cartId ?? cart?.id);
  if (!Number.isFinite(resolvedCartId)) return;

  const state = useCheckoutStore.getState();
  if (
    state.refundableCartId !== resolvedCartId ||
    state.quotedRefundableCartId === resolvedCartId ||
    state.refundableStatus === 'unavailable' ||
    state.isRefundableSelected ||
    state.refundableAccepted ||
    state.quotedRefundableAccepted ||
    state.refundableToken !== null ||
    (cart?.id != null && Number(cart.id) !== resolvedCartId)
  ) {
    return;
  }

  useCheckoutStore.setState({
    quotedRefundableCartId: resolvedCartId,
    quotedRefundableAccepted: quoted.accepted,
    refundableAccepted: false,
    isRefundableSelected: !quoted.accepted,
    refundablePrice: quoted.accepted ? quoted.price : 0,
    refundableToken: null,
  });
}
